import React, { useState, useEffect } from "react";
import {
  Stack,
  Checkbox,
  TextInput,
  Text,
  Select,
  MultiSelect,
  SimpleGrid,
  Divider,
  Paper,
} from "@mantine/core";
import RemoteSelect2 from "../remoteSelect2";
import McaNotificationTemplateInput from "./McaNotificationTemplateInput";
import {
  defaultStepNotification,
  formatStaffLabel,
  getMcaNotificationTemplateVars,
  MCA_NOTIFY_BY,
  MCA_NOTIFY_CHANNELS,
} from "../../data/mcaEntities";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";

const SectionTitle = ({ children }) => (
  <Text size="sm" weight={600}>
    {children}
  </Text>
);

const CLIENT_LINK_ENTITIES = new Set(["client", "order", "debitNote"]);

const McaStepNotificationForm = ({ value, onChange, targetEntity }) => {
  const readOnly = useMcaEditorReadOnly();
  const notification = { ...defaultStepNotification(), ...value };
  const templateVariables = getMcaNotificationTemplateVars(targetEntity);
  const [emailOptions, setEmailOptions] = useState(() => notification.notifyEmails ?? []);
  const showEntityUser = targetEntity === "user";
  const showEntityClient = CLIENT_LINK_ENTITIES.has(targetEntity);
  const showEntityConsultants = CLIENT_LINK_ENTITIES.has(targetEntity);
  const entityClientChecked =
    !!notification.notifyEntityClient ||
    (targetEntity === "client" && !!notification.notifyEntityUser);

  useEffect(() => {
    setEmailOptions((prev) => {
      const merged = [...prev];
      (notification.notifyEmails ?? []).forEach((e) => {
        if (e && !merged.includes(e)) merged.push(e);
      });
      return merged;
    });
  }, [notification.notifyEmails]);

  const patch = (p) => {
    if (readOnly) return;
    onChange({ ...notification, ...p });
  };

  const handleNotifyByChange = (notifyBy) => {
    patch({
      notifyBy,
      notifyEmails: notifyBy === "EMAILS" ? notification.notifyEmails : [],
      notifyUsers: notifyBy === "USERS" ? notification.notifyUsers : [],
      notifyRoles: notifyBy === "USER_ROLE" ? notification.notifyRoles : [],
    });
  };

  const isEmail = (notification.channel ?? "EMAIL") === "EMAIL";

  return (
    <Stack spacing="lg">
      <Stack spacing="md">
        <SectionTitle>Basic</SectionTitle>
        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
          <TextInput
            label="Name"
            required
            size="sm"
            placeholder="Notify maker on complete"
            value={notification.name ?? ""}
            readOnly={readOnly}
            onChange={(e) => patch({ name: e.target.value })}
          />
          <Select
            label="Channel"
            size="sm"
            data={MCA_NOTIFY_CHANNELS}
            value={notification.channel ?? "EMAIL"}
            readOnly={readOnly}
            onChange={(v) => patch({ channel: v ?? "EMAIL" })}
          />
        </SimpleGrid>
      </Stack>

      <Divider />

      <Stack spacing="md">
        <SectionTitle>When to send</SectionTitle>
        <Stack spacing="xs">
          <Checkbox
            label="Send on start of the step"
            checked={!!notification.notifyOnStart}
            disabled={readOnly}
            onChange={(e) => patch({ notifyOnStart: e.currentTarget.checked })}
          />
          <Checkbox
            label="Send on completion of the step"
            checked={notification.notifyOnComplete !== false}
            disabled={readOnly}
            onChange={(e) => patch({ notifyOnComplete: e.currentTarget.checked })}
          />
        </Stack>
      </Stack>

      <Divider />

      <Stack spacing="md">
        <SectionTitle>Recipients</SectionTitle>
        <Select
          label="Notify to"
          size="sm"
          data={MCA_NOTIFY_BY}
          value={notification.notifyBy ?? "USERS"}
          readOnly={readOnly}
          onChange={handleNotifyByChange}
        />
        {notification.notifyBy === "EMAILS" && (
          <MultiSelect
            label="Email addresses"
            size="sm"
            placeholder="Type email and press Enter"
            data={emailOptions}
            searchable
            creatable
            value={notification.notifyEmails ?? []}
            readOnly={readOnly}
            getCreateLabel={(query) => `+ Add ${query}`}
            onCreate={(query) => {
              const trimmed = query.trim();
              if (!trimmed) return null;
              setEmailOptions((current) =>
                current.includes(trimmed) ? current : [...current, trimmed]
              );
              return trimmed;
            }}
            onChange={(notifyEmails) => patch({ notifyEmails })}
          />
        )}
        {notification.notifyBy === "USERS" && (
          <RemoteSelect2
            form={{
              values: notification,
              setFieldValue: (field, val) => patch({ [field]: val }),
              markUserEdited: () => {},
            }}
            name="notifyUsers"
            label="Notify users"
            apiEntity="user"
            labelRender={formatStaffLabel}
            multiple
            size="sm"
            readOnly={readOnly}
            placeholder="Search users"
            searchFields={["name", "staffId", "cname", "email"]}
          />
        )}
        {notification.notifyBy === "USER_ROLE" && (
          <RemoteSelect2
            form={{
              values: notification,
              setFieldValue: (field, val) => patch({ [field]: val }),
              markUserEdited: () => {},
            }}
            name="notifyRoles"
            label="Notify roles"
            apiEntity="userRole"
            labelField="name"
            multiple
            size="sm"
            readOnly={readOnly}
            placeholder="Search roles"
            searchFields={["name"]}
          />
        )}
        <Paper withBorder p="sm" radius="sm">
          <Text size="xs" weight={600} mb="xs" color="dimmed">
            Additional recipients
          </Text>
          <Stack spacing="xs">
            <Checkbox
              label="Maker"
              checked={!!notification.notifyMaker}
              disabled={readOnly}
              onChange={(e) => patch({ notifyMaker: e.currentTarget.checked })}
            />
            <Checkbox
              label="Next step approvers"
              checked={!!notification.notifyNextApprovers}
              disabled={readOnly}
              onChange={(e) =>
                patch({ notifyNextApprovers: e.currentTarget.checked })
              }
            />
            {showEntityUser && (
              <Checkbox
                label="Entity user"
                description="Sends to the User login email (email field), not personal email"
                checked={!!notification.notifyEntityUser}
                disabled={readOnly}
                onChange={(e) =>
                  patch({ notifyEntityUser: e.currentTarget.checked })
                }
              />
            )}
            {showEntityClient && (
              <Checkbox
                label="Entity client"
                description="Sends to the client's first Contact Info email (default if marked), then related-person contact"
                checked={entityClientChecked}
                disabled={readOnly}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  patch({
                    notifyEntityClient: checked,
                    ...(targetEntity === "client"
                      ? { notifyEntityUser: false }
                      : {}),
                  });
                }}
              />
            )}
            {showEntityConsultants && (
              <Checkbox
                label="Entity consultants"
                description="Sends to all linked consultants' login emails"
                checked={!!notification.notifyEntityConsultants}
                disabled={readOnly}
                onChange={(e) =>
                  patch({ notifyEntityConsultants: e.currentTarget.checked })
                }
              />
            )}
          </Stack>
        </Paper>
      </Stack>

      {isEmail && (
        <>
          <Divider />
          <Stack spacing="md">
            <SectionTitle>Email content</SectionTitle>
            <McaNotificationTemplateInput
              label="Email subject"
              required
              placeholder="Step completed: {{requestCode}}"
              value={notification.subject ?? ""}
              readOnly={readOnly}
              onChange={(subject) => patch({ subject })}
              variables={templateVariables}
            />
            <McaNotificationTemplateInput
              label="Email message"
              required
              multiline
              placeholder="Request {{requestCode}} — step {{stepName}}"
              value={notification.message ?? ""}
              readOnly={readOnly}
              onChange={(message) => patch({ message })}
              variables={templateVariables}
            />
          </Stack>
        </>
      )}

      {!isEmail && (
        <Text size="sm" color="dimmed">
          SMS delivery is not configured yet. This rule will be saved but not sent.
        </Text>
      )}
    </Stack>
  );
};

export default McaStepNotificationForm;
